// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import ShinyText from '@/components/ui/ShinyText';
import { MorphIcon } from '@/components/ui/morph-icon';
import type { RemoteExecutionMode } from '@web/lib/remoteAgentProjection';
import type {
  AgentCallItem,
  AgentExecutionView,
  ToolCallItem,
  WorkItem,
} from '@web/lib/remoteWorkItemModel';
import { Users } from 'lucide';
import { AgentMarkdownStep, AgentToolAction } from './AgentFlatStep';

type DelegationGroup = {
  id: string;
  firstSequence: number;
  calls: AgentCallItem[];
};

type DisplayRow =
  | { type: 'delegation'; key: string; group: DelegationGroup }
  | { type: 'tool_action'; key: string; items: ToolCallItem[] }
  | {
      type: 'markdown';
      key: string;
      item: Exclude<WorkItem, AgentCallItem | ToolCallItem>;
    };

function isAgentActive(agent: AgentExecutionView | undefined): boolean {
  return Boolean(
    agent &&
    (agent.status === 'queued' ||
      agent.status === 'in_progress' ||
      agent.status === 'waiting')
  );
}

function delegationGroups(
  calls: AgentCallItem[],
  agentsById: Record<string, AgentExecutionView>
): DelegationGroup[] {
  const groups = new Map<string, DelegationGroup>();
  for (const call of calls) {
    const child = agentsById[call.childAgentExecutionKey];
    const id = child?.parallelGroupId || call.key;
    const current = groups.get(id);
    if (current) {
      current.calls.push(call);
      current.firstSequence = Math.min(
        current.firstSequence,
        call.firstSequence
      );
    } else {
      groups.set(id, {
        id,
        firstSequence: call.firstSequence,
        calls: [call],
      });
    }
  }
  return [...groups.values()].sort(
    (left, right) =>
      left.firstSequence - right.firstSequence ||
      left.id.localeCompare(right.id)
  );
}

function DelegationRow({
  group,
  agentsById,
  mode,
}: {
  group: DelegationGroup;
  agentsById: Record<string, AgentExecutionView>;
  mode: RemoteExecutionMode;
}) {
  const children = group.calls
    .map((call) => agentsById[call.childAgentExecutionKey])
    .filter((agent): agent is AgentExecutionView => Boolean(agent));
  const activeCount = children.filter(isAgentActive).length;
  const count = Math.max(children.length, group.calls.length);
  const noun = mode === 'workforce' ? 'worker' : 'subagent';
  const names = children.map((agent) => agent.name).join(', ');
  const state = activeCount
    ? `${activeCount} ${activeCount === 1 ? noun : `${noun}s`} running`
    : `${count} ${count === 1 ? noun : `${noun}s`} finished`;
  const label = names ? `${state} · ${names}` : state;

  return (
    <div
      className="flex min-h-9 w-fit max-w-full items-center gap-2.5 self-start rounded-lg bg-ds-bg-neutral-default-default px-2.5 py-1.5"
      data-agent-delegation={group.id}
      aria-label={label}
    >
      <MorphIcon
        icon={Users}
        aria-hidden
        className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
      />
      {activeCount > 0 ? (
        <ShinyText
          text={label}
          speed={2.4}
          className="block min-w-0 truncate text-body-sm"
        />
      ) : (
        <span className="min-w-0 truncate text-body-sm text-ds-text-neutral-muted-default">
          {label}
        </span>
      )}
    </div>
  );
}

export function AgentWorkForest({
  rootExecutionKeys,
  agentsById,
  itemsById,
  mode,
  turnFinalAnswerItemKey,
  expandedDetailKeys,
  onDetailExpandedChange,
}: {
  rootExecutionKeys: string[];
  agentsById: Record<string, AgentExecutionView>;
  itemsById: Record<string, WorkItem>;
  mode: RemoteExecutionMode;
  turnFinalAnswerItemKey?: string | null;
  expandedDetailKeys?: ReadonlySet<string>;
  onDetailExpandedChange?: (itemKey: string, expanded: boolean) => void;
}) {
  const rootAgentKeys = new Set(rootExecutionKeys);
  const items = Object.values(itemsById)
    .filter(
      (item) =>
        item.key !== turnFinalAnswerItemKey &&
        !(item.type === 'human_input' && item.status === 'waiting') &&
        !(
          item.type === 'agent_message' &&
          item.phase === 'final_answer' &&
          !rootAgentKeys.has(item.agentExecutionKey)
        )
    )
    .sort(
      (left, right) =>
        left.firstSequence - right.firstSequence ||
        left.key.localeCompare(right.key)
    );
  const groups = delegationGroups(
    items.filter((item): item is AgentCallItem => item.type === 'agent_call'),
    agentsById
  );
  const firstCallKeys = new Map(
    groups.map((group) => [group.calls[0].key, group])
  );
  const showAgentNames = Object.keys(agentsById).length > 1;
  const rows: DisplayRow[] = [];

  for (const item of items) {
    if (item.type === 'agent_call') {
      const group = firstCallKeys.get(item.key);
      if (group) {
        rows.push({
          type: 'delegation',
          key: `delegation:${group.id}`,
          group,
        });
      }
      continue;
    }

    if (item.type === 'tool_call') {
      const previous = rows.at(-1);
      const previousTool =
        previous?.type === 'tool_action' ? previous.items[0] : undefined;
      if (
        previous?.type === 'tool_action' &&
        previousTool?.agentExecutionKey === item.agentExecutionKey &&
        previousTool.toolName.trim().toLowerCase() ===
          item.toolName.trim().toLowerCase()
      ) {
        previous.items.push(item);
      } else {
        rows.push({ type: 'tool_action', key: item.key, items: [item] });
      }
      continue;
    }

    rows.push({ type: 'markdown', key: item.key, item });
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-0.5"
      aria-label={
        mode === 'workforce' ? 'Workforce agent steps' : 'Agent steps'
      }
    >
      {rows.map((row) => {
        if (row.type === 'delegation') {
          return (
            <DelegationRow
              key={row.key}
              group={row.group}
              agentsById={agentsById}
              mode={mode}
            />
          );
        }

        if (row.type === 'tool_action') {
          const first = row.items[0];
          return (
            <AgentToolAction
              key={row.key}
              items={row.items}
              agent={agentsById[first.agentExecutionKey]}
              showAgentName={showAgentNames}
              expanded={expandedDetailKeys?.has(row.key) || false}
              onExpandedChange={(expanded) =>
                onDetailExpandedChange?.(row.key, expanded)
              }
            />
          );
        }

        return (
          <AgentMarkdownStep
            key={row.key}
            item={row.item}
            agent={agentsById[row.item.agentExecutionKey]}
            rootAgentKeys={rootAgentKeys}
          />
        );
      })}

      {rows.length === 0 && (
        <p className="m-0 px-1 py-2 text-body-sm text-ds-text-neutral-muted-default">
          Waiting for agent activity…
        </p>
      )}
    </div>
  );
}
